// Analyst Portfolios (Drummond internal only): the investment team's analyst books
// against DS70, laid out like the Portfolio Viewer (its CSS is imported, not edited).
// DS70 = the trading app's approved set; analyst holdings = this app's own saved sets,
// edited on Manage Portfolio under the DS70 mandate rules; performance and attribution
// come from the books' Refinitiv reports in the attribution DB.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import api, { trackActivity } from '../../services/api';
import ResearchReportModal from '../../components/ResearchReportModal';
import '../PortfolioViewerApp.css';
import './AnalystPortfoliosApp.css';
import { API, apiError, downloadBlob, filenameFrom, useApiGet } from './apUtils';
import { Notice } from './apComponents';
import TargetWeightsTab from './TargetWeightsTab';
import PerformanceTab from './PerformanceTab';
import AllocationHistoryTab from './AllocationHistoryTab';
import TaaTiltsTab from './TaaTiltsTab';
import HoldingsHistoryTab from './HoldingsHistoryTab';
import AttributionTab from './AttributionTab';
import ChangesTab from './ChangesTab';
import ManagePortfolioTab from './ManagePortfolioTab';
import SummaryTab from './SummaryTab';
import { exportWorkbook } from './exportExcel';

const TABS = [
  ['summary', 'Summary'],
  ['holdings', 'Target Weights'], ['performance', 'Performance'], ['history', 'Allocation History'],
  ['tilts', 'TAA Tilts'], ['holdings-history', 'Holdings History'], ['changes', 'Changes'],
  ['attribution', 'Attribution'], ['manage', 'Manage Portfolio'],
];

export default function AnalystPortfoliosApp() {
  useEffect(() => { trackActivity('app_access', 'analyst_portfolios'); }, []);

  const [reload, setReload] = useState(0);        // a set was saved: books + holdings
  const [feeVersion, setFeeVersion] = useState(0);  // a fee was changed: holdings only
  const holdingsToken = `${reload}.${feeVersion}`;
  const booksState = useApiGet(`${API}/portfolios`, null, reload, true);
  const books = useMemo(() => booksState.data || [], [booksState.data]);
  const [picked, setPicked] = useState(null);           // null = every book
  const selected = picked ?? books.map((b) => b.code);
  const [tab, setTab] = useState('summary');
  const [focus, setFocus] = useState('');
  const [openReports, setOpenReports] = useState([]);
  const [busy, setBusy] = useState(null);
  const [exportError, setExportError] = useState(null);
  const containerRef = useRef(null);
  const tableRef = useRef(null);

  const analystBooks = books.filter((b) => b.kind === 'analyst');
  const singleBooks = analystBooks.some((b) => selected.includes(b.code))
    ? analystBooks.filter((b) => selected.includes(b.code)) : analystBooks;
  const focusCode = singleBooks.some((b) => b.code === focus) ? focus : singleBooks[0]?.code;

  const columnCodes = books.filter((b) => selected.includes(b.code)).map((b) => b.code).join(',');
  const holdings = useApiGet(columnCodes ? `${API}/holdings` : null, { codes: columnCodes }, holdingsToken, true);
  const reports = useApiGet('/api/portfolio/funds-with-reports');
  const fundsWithReports = useMemo(() => new Set(reports.data?.apirs || []), [reports.data]);

  // Fund returns for the Target Weights columns (same source as the viewer: the screener).
  const [perf, setPerf] = useState({ data: {}, error: null });
  useEffect(() => {
    const tr = [...new Set((holdings.data?.holdings || []).map((h) => h.tr_code).filter((t) => t && t !== '.ONEAUD'))];
    if (!tr.length) return undefined;
    let live = true;
    api.post('/api/screener/funds-by-tr-codes', { tr_codes: tr })
      .then((r) => { if (live) setPerf({ data: r.data.funds || {}, error: null }); })
      .catch((e) => { if (live) setPerf({ data: {}, error: apiError(e) }); });
    return () => { live = false; };
  }, [holdings.data]);

  const toggleBook = (code) => {
    const cur = picked ?? books.map((b) => b.code);
    setPicked(cur.includes(code) ? cur.filter((c) => c !== code) : books.map((b) => b.code).filter((c) => c === code || cur.includes(c)));
  };

  const exportImage = async () => {
    const node = tableRef.current;
    if (!node) return;
    const host = document.createElement('div');
    Object.assign(host.style, { position: 'absolute', left: '-10000px', top: '0', background: '#ffffff',
      padding: '16px', width: 'max-content' });
    const clone = node.cloneNode(true);
    clone.querySelectorAll('th.perf-col, td.perf-col, th.report-col, td.report-col, th.asat-col, td.asat-col')
      .forEach((el) => el.remove());
    clone.querySelectorAll('td[colspan="11"]').forEach((td) => { td.colSpan = 2; });
    host.appendChild(clone);
    (containerRef.current || document.body).appendChild(host);
    try {
      const canvas = await html2canvas(host, { backgroundColor: '#ffffff', scale: 2 });
      const a = document.createElement('a');
      a.download = 'Analyst_Portfolios_Holdings.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    } finally {
      host.remove();
    }
  };

  const exportExcel = async () => {
    setBusy('excel'); setExportError(null);
    try {
      const hist = focusCode ? (await api.get(`${API}/portfolios/${focusCode}/holdings-history`)).data : null;
      exportWorkbook({ holdings: holdings.data, history: hist, historyName: focusCode,
        filename: 'Analyst_Portfolios_Export.xlsx' });
    } catch (e) {
      setExportError(`Excel export failed: ${apiError(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const exportRefinitiv = async () => {
    setBusy('refinitiv'); setExportError(null);
    try {
      const r = await api.get(`${API}/export/refinitiv.xlsx`, { responseType: 'blob' });
      downloadBlob(r.data, filenameFrom(r.headers, 'Refinitiv Upload.xlsx'));
    } catch (e) {
      let msg = apiError(e);
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep msg */ }
      }
      setExportError(`Refinitiv export failed: ${msg}`);
    } finally {
      setBusy(null);
    }
  };

  if (booksState.loading && !books.length) {
    return <div className="portfolio-viewer-container analyst-portfolios-app"><div className="loading-spinner">Loading analyst portfolios...</div></div>;
  }

  const single = { code: focusCode, setCode: setFocus, books: singleBooks };

  return (
    <div className="portfolio-viewer-container analyst-portfolios-app" ref={containerRef}>
      <div className="viewer-header">
        <div className="header-left"><h2>Analyst Portfolios</h2></div>
        <div className="header-filters">
          <button type="button" className="select-all-btn" onClick={exportRefinitiv} disabled={busy === 'refinitiv'}
            title="Every saved set of every analyst book, one sheet per book, in the Refinitiv PORT upload format">
            {busy === 'refinitiv' ? 'Exporting…' : 'Export Refinitiv Upload'}
          </button>
        </div>
      </div>

      {booksState.error && <Notice kind="error">{booksState.error}</Notice>}
      {exportError && <Notice kind="error">{exportError}</Notice>}

      <div className="pviewer-model-row">
        {books.map((b) => (
          <div key={b.code} className={`pviewer-model-chip${selected.includes(b.code) ? ' on' : ''}`}>
            <button type="button" className="pviewer-model-label" onClick={() => toggleBook(b.code)}
              title={b.kind === 'benchmark' ? 'DS70 (DRU007SU): the trading app\'s approved set' : `${b.refinitiv_name}`}>
              {b.name}
            </button>
          </div>
        ))}
      </div>

      <div className="tabs-container">
        <div className="tab-nav">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" className={`tab-btn ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
          {tab === 'holdings' && holdings.data?.holdings?.length > 0 && (
            <button type="button" className="tab-btn export-btn" onClick={exportImage}
              title="Download the holdings table as an image (without the fund performance and report columns)">Export Image</button>
          )}
          {holdings.data?.holdings?.length > 0 && (
            <button type="button" className="tab-btn export-btn" onClick={exportExcel} disabled={busy === 'excel'}
              title="Holdings for the selected books, plus the selected book's holdings history">Export Excel</button>
          )}
        </div>

        <div className="tab-content">
          {tab === 'summary' && <SummaryTab books={books} reload={holdingsToken} />}
          {tab === 'holdings' && (
            <div className="tab-panel">
              {holdings.error && <Notice kind="error">{holdings.error}</Notice>}
              {perf.error && <Notice kind="warn">Fund returns could not be loaded: {perf.error}</Notice>}
              {holdings.loading && !holdings.data && <div className="loading-spinner">Loading holdings...</div>}
              {!columnCodes && <div className="no-selection"><p>Select one or more books.</p></div>}
              {holdings.data && (
                <TargetWeightsTab data={holdings.data} screenerPerf={perf.data} fundsWithReports={fundsWithReports}
                  tableRef={tableRef}
                  onOpenReport={(apir) => setOpenReports((prev) => (prev.includes(apir) ? prev : [...prev, apir]))} />
              )}
            </div>
          )}
          {tab === 'performance' && <PerformanceTab books={books} />}
          {tab === 'history' && <AllocationHistoryTab {...single} />}
          {tab === 'tilts' && <TaaTiltsTab {...single} />}
          {tab === 'holdings-history' && <HoldingsHistoryTab {...single} />}
          {tab === 'changes' && <ChangesTab key={reload} />}
          {tab === 'attribution' && <AttributionTab {...single} />}
          {tab === 'manage' && (
            <ManagePortfolioTab books={books} onSaved={() => setReload((x) => x + 1)}
              onFeesChanged={() => setFeeVersion((x) => x + 1)} />
          )}
        </div>
      </div>

      {openReports.map((apir) => (
        <ResearchReportModal key={apir} isOpen onClose={() => setOpenReports((prev) => prev.filter((a) => a !== apir))}
          apirCode={apir} />
      ))}
    </div>
  );
}
